// One emulated device: a Pocket app running in its own iframe realm with its
// own wasm UI core, stepped at 60 Hz and painted into the canvases the device
// model shows on its screens. The realm API is hosts/web/app-instance.js.

import { __packTouchWide, createTouchHitFacts } from "../../vendor/pocketjs/framework/src/touch.ts";
import { SandboxRealm } from "./sandbox.ts";

export const BTN = {
  SELECT: 0x0001,
  START: 0x0008,
  UP: 0x0010,
  RIGHT: 0x0020,
  DOWN: 0x0040,
  LEFT: 0x0080,
  LTRIGGER: 0x0100,
  RTRIGGER: 0x0200,
  TRIANGLE: 0x1000,
  CIRCLE: 0x2000,
  CROSS: 0x4000,
  SQUARE: 0x8000,
} as const;

/** Button names as stage profiles spell them. */
export const BUTTON_BITS: Record<string, number> = {
  up: BTN.UP, right: BTN.RIGHT, down: BTN.DOWN, left: BTN.LEFT,
  l: BTN.LTRIGGER, r: BTN.RTRIGGER,
  triangle: BTN.TRIANGLE, circle: BTN.CIRCLE, cross: BTN.CROSS, square: BTN.SQUARE,
  select: BTN.SELECT, start: BTN.START,
};

/** The PocketJS keyboard convention: Enter or Z confirms, X goes back. */
export const KEY_BITS: Record<string, number> = {
  ArrowUp: BTN.UP, ArrowRight: BTN.RIGHT, ArrowDown: BTN.DOWN, ArrowLeft: BTN.LEFT,
  Enter: BTN.CIRCLE, KeyZ: BTN.CIRCLE, KeyX: BTN.CROSS, KeyA: BTN.SQUARE, KeyS: BTN.TRIANGLE,
  KeyQ: BTN.LTRIGGER, KeyE: BTN.RTRIGGER, KeyW: BTN.RTRIGGER,
  Space: BTN.START, ShiftLeft: BTN.SELECT, ShiftRight: BTN.SELECT,
};

export type Surface = "primary" | "auxiliary";

interface AppInstance {
  step(buttons: number, touches?: number[], hits?: number[], surfaces?: number[]): void;
  hitTestBounds(x: number, y: number, surface?: Surface): number;
  render(scale?: number): Uint8ClampedArray;
  renderAuxiliary(): Uint8ClampedArray;
  sendService(line: string): void;
  drainService(): string[];
  dispose(): void;
}

export interface RealmOptions {
  /**
   * Run the app as code nobody here vouches for: in a sandboxed frame with
   * no origin, no network and no service channel (./sandbox.ts). Games on the
   * shelf run this way; the room's own television does not.
   */
  untrusted?: boolean;
  /** Directory holding app-instance.html and the wasm core. */
  runtimeUrl: string;
  packageId: string;
  bundleUrl: string;
  pakUrl: string;
  viewport: [number, number];
  density: number;
  auxiliary?: [number, number];
  primary: HTMLCanvasElement;
  auxiliaryCanvas?: HTMLCanvasElement;
  /** Service channels the guest may open (the spec svc ops). */
  services?: string[];
  /** Called with each line the guest sent, after the step that sent it. */
  onLine?: (line: string) => void;
}

const STEP = 1000 / 60;

/** One finger or stylus on a surface: its number while it is down, and where it is in the surface's logical pixels. */
export interface Contact {
  id: number;
  x: number;
  y: number;
}

/**
 * Everything a device's controls hold at one moment, for a host that reads
 * them whole each frame (the web player). `left` and `right` are the sticks,
 * each -1 to 1 with right and up positive; a device without one leaves it
 * out. `contacts` are on `surface`.
 */
export interface HeldInput {
  buttons: number;
  left?: readonly [number, number];
  right?: readonly [number, number];
  surface?: Surface;
  contacts?: readonly Contact[];
}

/** The analog stick at rest, as `frame` takes it: (x << 8) | y, each 0 to 255 with 128 the centre. */
export const ANALOG_CENTRE = 0x8080;

/** A stick of -1 to 1, right and up positive, as `frame` takes it: x then y, y growing downward. */
export function packStick(stick: readonly [number, number] | undefined): number {
  if (!stick) return ANALOG_CENTRE;
  const axis = (value: number) => Math.max(0, Math.min(255, Math.round(128 + Math.max(-1, Math.min(1, value)) * 127)));
  return (axis(stick[0]) << 8) | axis(-stick[1]);
}

/** Contacts as a realm keeps them: whole pixels inside the surface, at most eight, each number once. */
export function clampContacts(contacts: readonly Contact[] | undefined, size: readonly [number, number]): Contact[] {
  const seen = new Set<number>();
  const kept: Contact[] = [];
  for (const contact of contacts ?? []) {
    const id = contact.id & 0xff;
    if (seen.has(id) || kept.length === 8) continue;
    seen.add(id);
    kept.push({
      id,
      x: Math.max(0, Math.min(size[0] - 1, Math.round(contact.x))),
      y: Math.max(0, Math.min(size[1] - 1, Math.round(contact.y))),
    });
  }
  return kept;
}

/** A running app as the room drives it, whichever kind of frame it runs in. */
export interface RunningRealm {
  /** Frames the app has run. */
  ticks: number;
  /**
   * Times the app was handed what is held: each turn carries the buttons,
   * sticks and contacts of that moment and runs one or more frames with them.
   */
  turns: number;
  /** Why the app stopped, once it has. */
  failed: Error | null;
  press(bit: number, down: boolean): void;
  /** A contact in logical pixels of the given surface; null lifts it. */
  touch(surface: Surface, point: [number, number] | null): void;
  /**
   * Everything held from now on, in place of what `press` and `touch` set.
   * The sandboxed realm hands the app both sticks. The realm of the room's
   * own apps hands it none: PocketJS's AppInstance gives its guest a centred
   * stick.
   */
  hold(input: HeldInput): void;
  release(): void;
  /** Queue a service line for the guest. A sandboxed app has no channel and drops it. */
  send(line: object): void;
  /** Run `fn` once the guest has consumed one more frame of input. */
  afterNextTick(fn: () => void): void;
  /** Advance by wall-clock `dt` ms; true when the screens were repainted. */
  advance(dt: number): boolean;
  dispose(): void;
}

export class DeviceRealm {
  private held = 0;
  private surface: Surface = "primary";
  private contacts: Contact[] = [];
  private accumulator = 0;
  private outputs: Array<{ context: CanvasRenderingContext2D; image: ImageData; auxiliary: boolean }> = [];
  private hits: (packed: readonly number[] | undefined) => number[] | undefined;
  private afterTick = new Set<() => void>();
  ticks = 0;
  turns = 0;
  failed: Error | null = null;

  private constructor(private iframe: HTMLIFrameElement, private api: AppInstance, private options: RealmOptions) {
    const { primary, auxiliaryCanvas, viewport, density, auxiliary } = options;
    primary.width = viewport[0] * density;
    primary.height = viewport[1] * density;
    const context = primary.getContext("2d")!;
    this.outputs.push({ context, image: new ImageData(primary.width, primary.height), auxiliary: false });
    if (auxiliary && auxiliaryCanvas) {
      auxiliaryCanvas.width = auxiliary[0];
      auxiliaryCanvas.height = auxiliary[1];
      this.outputs.push({
        context: auxiliaryCanvas.getContext("2d")!,
        image: new ImageData(auxiliary[0], auxiliary[1]),
        auxiliary: true,
      });
    }
    this.hits = createTouchHitFacts((x, y) => this.api.hitTestBounds(x, y, this.contacts.length ? this.surface : undefined));
    this.step();
    this.paint();
  }

  static async boot(options: RealmOptions): Promise<RunningRealm> {
    if (options.untrusted) return SandboxRealm.boot(options);
    const iframe = document.createElement("iframe");
    iframe.hidden = true;
    iframe.tabIndex = -1;
    iframe.setAttribute("aria-hidden", "true");
    const loaded = new Promise<void>((resolve, reject) => {
      iframe.addEventListener("load", () => resolve(), { once: true });
      iframe.addEventListener("error", () => reject(new Error("device runtime could not load")), { once: true });
    });
    iframe.src = options.runtimeUrl + "app-instance.html";
    document.body.append(iframe);
    try {
      await loaded;
      const factory = (iframe.contentWindow as unknown as { PocketAppInstance?: { create(o: object): Promise<AppInstance> } }).PocketAppInstance;
      if (!factory) throw new Error("device runtime did not publish PocketAppInstance");
      const api = await factory.create({
        packageId: options.packageId,
        viewport: options.viewport,
        rasterDensity: options.density,
        auxiliary: options.auxiliary,
        companions: options.services,
        wasmUrl: new URL(options.runtimeUrl + "pocketjs.wasm", location.href).href,
        bundleUrl: new URL(options.bundleUrl, location.href).href,
        pakUrl: new URL(options.pakUrl, location.href).href,
      });
      return new DeviceRealm(iframe, api, options);
    } catch (error) {
      iframe.remove();
      throw error;
    }
  }

  press(bit: number, down: boolean): void {
    this.held = down ? this.held | bit : this.held & ~bit;
  }

  private sizeOf(surface: Surface): [number, number] {
    return surface === "auxiliary" ? this.options.auxiliary ?? this.options.viewport : this.options.viewport;
  }

  /** A contact in logical pixels of the given surface; null lifts it. */
  touch(surface: Surface, point: [number, number] | null): void {
    this.surface = surface;
    this.contacts = point ? clampContacts([{ id: 0, x: point[0], y: point[1] }], this.sizeOf(surface)) : [];
  }

  /** Everything held from now on. The sticks are not handed on: AppInstance gives its guest a centred one. */
  hold(input: HeldInput): void {
    this.held = input.buttons;
    this.surface = input.surface ?? "primary";
    this.contacts = clampContacts(input.contacts, this.sizeOf(this.surface));
  }

  release(): void {
    this.held = 0;
    this.contacts = [];
  }

  /** Queue a line for the guest; it reads the queue at its next frame. */
  send(line: object): void {
    this.api.sendService(JSON.stringify(line));
  }

  /** Run `fn` once the guest has consumed one more frame of input. */
  afterNextTick(fn: () => void): void {
    this.afterTick.add(fn);
  }

  private step(): void {
    const surface = this.surface === "auxiliary" ? 1 : 0;
    const touches = this.contacts.length ? this.contacts.map((c) => __packTouchWide(c.id, c.x, c.y)) : undefined;
    this.api.step(this.held, touches, this.hits(touches), touches?.map(() => surface));
    this.ticks++;
    if (this.options.onLine) for (const line of this.api.drainService()) this.options.onLine(line);
    for (const fn of [...this.afterTick]) {
      this.afterTick.delete(fn);
      fn();
    }
  }

  private paint(): void {
    for (const output of this.outputs) {
      const pixels = output.auxiliary ? this.api.renderAuxiliary() : this.api.render(this.options.density);
      output.image.data.set(pixels);
      output.context.putImageData(output.image, 0, 0);
    }
  }

  /** Advance by wall-clock `dt` ms in fixed 60 Hz steps; true when the screens were repainted. */
  advance(dt: number): boolean {
    if (this.failed) return false;
    this.accumulator += Math.min(250, dt);
    let steps = 0;
    try {
      while (this.accumulator >= STEP && steps < 4) {
        this.step();
        this.accumulator -= STEP;
        steps++;
      }
      if (steps) {
        this.turns++;
        this.paint();
      }
    } catch (error) {
      this.failed = error instanceof Error ? error : new Error(String(error));
      console.error("device app stopped", error);
    }
    return steps > 0;
  }

  dispose(): void {
    this.afterTick.clear();
    try {
      this.api.dispose();
    } catch { /* the realm is going away regardless */ }
    this.iframe.remove();
  }
}
